import type { PostProcessor } from '@elfsight/lib-translator';
import {
  createPhraseExcludeHandler,
  translatePhrasesProcessor
} from '@elfsight/lib-translator';
import type { LanguageCode, TranslationExclusionValue } from '../../types';
import {
  applyCustomTranslations,
  type CustomTranslationsMap
} from '../utils/custom-translations';
import { collectPhrases, updateDOM } from './dom';
import { emailFinder } from './email-finder';
import { emailSetter } from './email-setter';

export type TranslatePageProps = {
  sourceLanguage?: string;
  targetLanguage: LanguageCode;
  element?: Node | Element;
  widgetToken?: string;
  translationExclusions: TranslationExclusionValue[];
  customTranslations?: CustomTranslationsMap;
};

const AUTO_DETECT_LANGUAGE = false;

export async function translatePage({
  sourceLanguage,
  targetLanguage,
  element = document.body,
  widgetToken,
  translationExclusions,
  customTranslations
}: TranslatePageProps) {
  try {
    const phrasesInfo = collectPhrases(element, translationExclusions);

    const excludedPhrases = translationExclusions.map(({ value }) => value);
    const phrases = phrasesInfo.map((info) => info.phrase);
    const customTranslationPlans = applyCustomTranslations({
      phrases,
      targetLanguage,
      customTranslations,
      excludedPhrases
    });
    const exactTranslatedPhrases = customTranslationPlans.map(
      ({ exactTranslation }) => exactTranslation
    );

    const excludeHandler = createPhraseExcludeHandler({
      excludedPhrases,
      customFilter: (value) => !value.startsWith('.')
    });

    const partialTranslationPlans = customTranslationPlans.filter(
      ({ exactTranslation }) => exactTranslation === null
    );
    const phrasesToTranslate = phrases.filter(
      (_, index) => exactTranslatedPhrases[index] === null
    );

    const requestParams = {
      sourceLanguage: AUTO_DETECT_LANGUAGE ? undefined : sourceLanguage,
      targetLanguage,
      widgetToken
    };
    const storeFilter = (phrase: string) => phrase.split(' ').length <= 3;
    const preProcessors = [
      () =>
        partialTranslationPlans.map(
          ({ phraseForTranslation }) => phraseForTranslation
        ),
      excludeHandler.exclude
    ];
    const postProcessors: PostProcessor[] = [
      excludeHandler.restore,
      (translatedPhrases) =>
        translatedPhrases.map((translatedPhrase, index) =>
          partialTranslationPlans[index]?.restoreTranslation(translatedPhrase)
        )
    ];

    const translatedRemainingPhrases =
      phrasesToTranslate.length > 0
        ? await translatePhrasesProcessor({
            phrases: phrasesToTranslate,
            requestParams,
            storeFilter,
            preProcessors,
            postProcessors
          })
        : [];

    let translatedRemainingIndex = 0;
    const translatedPhrases = exactTranslatedPhrases.map(
      (exactTranslatedPhrase, index) =>
        exactTranslatedPhrase ??
        translatedRemainingPhrases[translatedRemainingIndex++] ??
        phrases[index]
    );

    updateDOM(
      phrasesInfo,
      emailSetter(translatedPhrases, emailFinder(phrases))
    );
  } catch (error) {
    console.error(error);
  }
}
